import { lazy, Suspense, useState, useEffect } from 'react';
import SpotPriceTrigger from '@/components/pricing/SpotPriceTrigger';
const SpotPriceDialog = lazy(() => import('@/components/SpotPriceDialog'));

// Cache spot price across mounts/navigations so we don't hammer Strømligning.
// Keyed by postal code. TTL = 10 min (Strømligning prices are hourly anyway).
const CACHE_TTL_MS = 10 * 60 * 1000;
const cacheKey = (zip) => `spotprice_cache_${zip}`;

function readCache(zip) {
  try {
    const raw = sessionStorage.getItem(cacheKey(zip));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.ts > CACHE_TTL_MS) return null;
    return parsed.value;
  } catch { return null; }
}

function writeCache(zip, value) {
  try {
    sessionStorage.setItem(cacheKey(zip), JSON.stringify({ ts: Date.now(), value }));
  } catch { /* ignore quota errors */ }
}

// Fetch current spot price using the same API as PriceChart
async function fetchCurrentSpotPrice(zipCode = '2450') {
  try {
    // Step 1: resolve supplier from postal code (same as PriceChart)
    const supRes = await fetch(`https://stromligning.dk/api/suppliers/find?postalCode=${zipCode}`);
    let supplierId = 'radius_c'; // default DK2
    if (supRes.ok) {
      const suppliers = await supRes.json();
      if (suppliers.length > 0) supplierId = suppliers[0].id;
    }

    // Step 2: fetch prices with same params as PriceChart
    const params = new URLSearchParams({ productId: 'enkel_energi-pioneers', supplierId, forecast: 'true', aggregation: '1h' });
    const priceRes = await fetch(`https://stromligning.dk/api/prices?${params}`);
    if (!priceRes.ok) throw new Error(`API error: ${priceRes.status}`);
    const result = await priceRes.json();

    if (!result.prices || result.prices.length === 0) throw new Error('No prices');

    // Step 3: find current hour (same logic as PriceChart)
    const now = new Date();
    const current = result.prices.slice().reverse().find(p => new Date(p.date) <= now) || result.prices[0];

    // Step 4: compute total with VAT (same as PriceChart default settings)
    const d = current.details;
    const totalWithoutVat =
      (d.electricity?.value || 0) +
      (d.surcharge?.value || 0) +
      (d.distribution?.value || 0) +
      (d.electricityTax?.value || 0) +
      (d.transmission?.systemTariff?.value || 0) +
      (d.transmission?.netTariff?.value || 0);
    const total = totalWithoutVat * 1.25;

    const value = { totalKr: total, time: current.date };
    writeCache(zipCode, value);
    return value;
  } catch (err) {
    console.error('SpotPrice fetch error:', err);
    return null;
  }
}

export default function SpotPriceTicker({ className = '', lang = 'da' }) {
  const isEn = lang === 'en';
  const [price, setPrice] = useState(null);
  const [open, setOpen] = useState(false);
  const [postalCode, setPostalCode] = useState('2450');

  // Show the cached price instantly (avoids a network call on every mount /
  // navigation), and refresh from the API at most once per 10 minutes. This
  // prevents hammering Strømligning's endpoint (which returns 429 under load).
  useEffect(() => {
    let cancelled = false;

    // Serve cache immediately if present
    const cached = readCache(postalCode);
    if (cached) setPrice(cached);

    // Only hit the API if there is no fresh cache for this postal code
    const refreshIfStale = () => {
      if (readCache(postalCode)) return; // still fresh, skip the network call
      fetchCurrentSpotPrice(postalCode).then((p) => { if (!cancelled && p) setPrice(p); });
    };
    // Secondary pricing must not contend with the page's critical resources.
    let initialTimer, initialIdle;
    const scheduleInitial = () => {
      if (window.requestIdleCallback) initialIdle = window.requestIdleCallback(refreshIfStale, { timeout: 2000 });
      else initialTimer = setTimeout(refreshIfStale, 300);
    };
    if (document.readyState === 'complete') scheduleInitial();
    else window.addEventListener('load', scheduleInitial, { once: true });

    // Poll every 10 minutes — the cache guard above ensures we only actually
    // fetch when the cached value has expired.
    const intervalId = setInterval(refreshIfStale, 10 * 60 * 1000);

    // On tab refocus, only refresh if the cache has gone stale
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshIfStale();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      window.removeEventListener('load', scheduleInitial);
      if (initialIdle != null) window.cancelIdleCallback(initialIdle);
      clearTimeout(initialTimer);
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [postalCode]);

  // Prevent body scroll when modal open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const displayPrice = price ? price.totalKr.toFixed(2).replace('.', ',') : '–';

  return (
    <>
      <SpotPriceTrigger en={isEn} displayPrice={displayPrice} open={open} onClick={() => setOpen(true)} className={className} />

      {/* ── Modal (via Portal into modal-root, outside header stacking context) ── */}
      {open && <Suspense fallback={<span role="status" className="sr-only">{isEn ? 'Loading prices' : 'Henter priser'}</span>}><SpotPriceDialog lang={lang} postalCode={postalCode} onPostalCodeChange={setPostalCode} onClose={() => setOpen(false)} /></Suspense>}
    </>
  );
}